import React, { useCallback } from "react"
import { Heatmap } from "@nova/component-heatmap"
import { imageProcessingPresentation } from "@nova/component-selected-image-status"
import { useAppSelector } from "integrations/redux/hooks"
import { setActiveImageId, setHoveredImageId } from "features/map/layers/slice"
import { LoadingErrorMaybe, UploadPresentationOptions } from "@nova/constants"
import { GenericUpload } from "../../../../../plugins/shared/types"
import { selectActiveImageIds, selectHoveredImageID } from "features/map/layers/selectors"
import { useDebounceDispatch } from "tools/store"
import { useUploadFlowContext } from "../useUploadFlowContext"

type UploadFlowGridProps = {
    uploads: LoadingErrorMaybe<GenericUpload>[]
    hideErrorsFromHeatmap?: boolean
    hasExceededUploads?: boolean
    isOverflowed?: boolean
    removeSelected: () => Promise<void>
    uploadPresentationOptions: UploadPresentationOptions
}

export const UploadFlowGrid = ({
    uploads,
    isOverflowed,
    removeSelected,
    uploadPresentationOptions,
}: UploadFlowGridProps) => {
    const { imagePreviewType } = useUploadFlowContext()
    const { dispatch, debounceDispatch } = useDebounceDispatch(40)

    const activeImageIds = useAppSelector(selectActiveImageIds)
    const hoveredImageId = useAppSelector(selectHoveredImageID)

    const updateSelection = (newSelection: Record<string, boolean>, clickedId?: string) => {
        if (!imagePreviewType) return
        dispatch(setActiveImageId({ ids: newSelection, type: imagePreviewType, clickedId }))
    }

    const setHoveredImageIdCallback = useCallback(
        (id?: string) => {
            debounceDispatch(setHoveredImageId(id))
        },
        [debounceDispatch]
    )

    return (
        <Heatmap
            items={uploads}
            style={imageProcessingPresentation}
            selected={activeImageIds || new Set()}
            updateSelected={updateSelection}
            hoveredImageId={hoveredImageId}
            setHoveredImageId={setHoveredImageIdCallback}
            isOverflowed={isOverflowed}
            onDeleteImage={removeSelected}
            uploadPresentationOptions={uploadPresentationOptions}
        />
    )
}
